import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ChatIcon from "@material-ui/icons/Chat";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import { StoreOutlined, Contacts } from "@material-ui/icons";

import { i18n } from "../translate/i18n";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import api from "../services/api";
import toastError from "../errors/toastError";

const useStyles = makeStyles((theme) => ({
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    minHeight: 72,
    "& .MuiBottomNavigationAction-root": {
      minWidth: 56,
      paddingTop: theme.spacing(1),
    },
    "& .MuiBottomNavigationAction-label": {
      fontSize: "0.7rem",
      marginTop: 4,
    },
    "& .MuiBottomNavigationAction-root.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  navAction: {
    "& .MuiBottomNavigationAction-label": {
      fontSize: "0.7rem",
    },
  },
  ticketsAction: {
    flex: 1,
    maxWidth: "none",
    "& .MuiBottomNavigationAction-label": {
      fontSize: "0.8rem",
      fontWeight: 700,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 32,
    },
  },
  moreDrawer: {
    "& .MuiDrawer-paper": {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: "75vh",
      paddingBottom: theme.spacing(3),
    },
  },
  drawerHandle: {
    width: 48,
    height: 5,
    backgroundColor: theme.palette.divider,
    borderRadius: 3,
    margin: theme.spacing(2, "auto"),
  },
  drawerTitle: {
    padding: theme.spacing(2, 3),
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
  },
  listItem: {
    padding: theme.spacing(1.5, 3),
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  groupHeader: {
    padding: theme.spacing(1.5, 3),
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
  },
}));

const NavLink = ({ to, icon, primary, onClick, selected, classes }) => (
  <ListItem
    button
    component={RouterLink}
    to={to}
    onClick={onClick}
    selected={selected}
    className={classes?.listItem}
  >
    {icon && <ListItemIcon style={{ minWidth: 40 }}>{icon}</ListItemIcon>}
    <ListItemText primary={primary} primaryTypographyProps={{ variant: "body1" }} />
  </ListItem>
);

const BottomNav = ({ visible }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [moreOpen, setMoreOpen] = useState(false);
  const [settings, setSettings] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSettings();
  }, []);

  const getSettingValue = (key) => {
    const s = settings.find((x) => x.key === key);
    return s ? s.value : "";
  };

  const handleMoreClose = () => setMoreOpen(false);

  if (!visible) return null;

  const isTickets = location.pathname.startsWith("/tickets");
  const isInternalChats = location.pathname.startsWith("/internal-chats");
  const isDashboard = location.pathname === "/";
  const isContacts = location.pathname.startsWith("/contacts");
  const navValue = isTickets
    ? "/tickets"
    : isInternalChats
    ? "/internal-chats"
    : isDashboard
    ? "/"
    : isContacts
    ? "/contacts"
    : "more";

  return (
    <>
      <BottomNavigation
        value={navValue}
        onChange={(e, newValue) => {
          if (newValue === "more") setMoreOpen(true);
        }}
        showLabels
        className={classes.bottomNav}
      >
        <BottomNavigationAction
          label="Agentes"
          value="/internal-chats"
          icon={<ChatIcon />}
          component={RouterLink}
          to="/internal-chats"
          className={classes.navAction}
        />
        <BottomNavigationAction
          label="Dashboard"
          value="/"
          icon={<DashboardOutlinedIcon />}
          component={RouterLink}
          to="/"
          className={classes.navAction}
        />
        <BottomNavigationAction
          label={i18n.t("mainDrawer.listItems.tickets")}
          value="/tickets"
          icon={<WhatsAppIcon />}
          component={RouterLink}
          to="/tickets"
          className={classes.ticketsAction}
        />
        <BottomNavigationAction
          label={i18n.t("mainDrawer.listItems.contacts")}
          value="/contacts"
          icon={<Contacts />}
          component={RouterLink}
          to="/contacts"
          className={classes.navAction}
        />
        <BottomNavigationAction
          label={i18n.t("mainDrawer.listItems.settings")}
          value="more"
          icon={<SettingsOutlinedIcon />}
          onClick={() => setMoreOpen(true)}
          className={classes.navAction}
        />
      </BottomNavigation>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={handleMoreClose}
        className={classes.moreDrawer}
      >
        <div className={classes.drawerHandle} />
        <div className={classes.drawerTitle}>
          {i18n.t("mainDrawer.listItems.more") || "Más opciones"}
        </div>
        <List>
          <NavLink
            to="/connections"
            icon={<SyncAltIcon />}
            primary={i18n.t("mainDrawer.listItems.connections")}
            onClick={handleMoreClose}
            selected={location.pathname === "/connections"}
            classes={classes}
          />
          <NavLink
            to="/contacts"
            icon={<Contacts />}
            primary={i18n.t("mainDrawer.listItems.contacts")}
            onClick={handleMoreClose}
            selected={location.pathname === "/contacts"}
            classes={classes}
          />
          <NavLink
            to="/quickAnswers"
            icon={<QuestionAnswerOutlinedIcon />}
            primary={i18n.t("mainDrawer.listItems.quickAnswers")}
            onClick={handleMoreClose}
            selected={location.pathname === "/quickAnswers"}
            classes={classes}
          />
          {getSettingValue("storeEnabled") === "enabled" && (
            <NavLink
              to="/catalog"
              icon={<StoreOutlined />}
              primary="Catálogo"
              onClick={handleMoreClose}
              selected={location.pathname === "/catalog"}
              classes={classes}
            />
          )}
          <Can
            role={user.profile}
            perform="drawer-admin-items:view"
            yes={() => (
              <>
                <Divider style={{ margin: "16px 0" }} />
                <div className={classes.groupHeader}>
                  {i18n.t("mainDrawer.listItems.administration")}
                </div>
                <NavLink
                  to="/users"
                  icon={<PeopleAltOutlinedIcon />}
                  primary={i18n.t("mainDrawer.listItems.users")}
                  onClick={handleMoreClose}
                  selected={location.pathname === "/users"}
                  classes={classes}
                />
                <NavLink
                  to="/Queues"
                  icon={<AccountTreeOutlinedIcon />}
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  onClick={handleMoreClose}
                  selected={location.pathname === "/Queues"}
                  classes={classes}
                />
                <NavLink
                  to="/Settings"
                  icon={<SettingsOutlinedIcon />}
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  onClick={handleMoreClose}
                  selected={location.pathname === "/Settings"}
                  classes={classes}
                />
                <NavLink
                  to="/Statistics"
                  icon={<EqualizerIcon />}
                  primary="Estadísticas"
                  onClick={handleMoreClose}
                  selected={location.pathname === "/Statistics"}
                  classes={classes}
                />
              </>
            )}
          />
        </List>
      </Drawer>
    </>
  );
};

export default BottomNav;
